import { Capacitor } from '@capacitor/core';

/** Return `true` when the hardware back press was handled (do not fall through). */
export type NativeHardwareBackHandler = () => boolean;

const handlers: NativeHardwareBackHandler[] = [];
let listenerInstalled = false;
let removeCapacitorListener: (() => void) | null = null;

/** Registers a handler (most recently registered runs first). */
export function registerNativeHardwareBackHandler(handler: NativeHardwareBackHandler): () => void {
  handlers.push(handler);
  void ensureNativeHardwareBackListener();
  return () => {
    const index = handlers.indexOf(handler);
    if (index >= 0) handlers.splice(index, 1);
  };
}

function dispatchNativeHardwareBack(canGoBack: boolean): void {
  for (let i = handlers.length - 1; i >= 0; i--) {
    if (handlers[i]()) return;
  }
  if (canGoBack) {
    globalThis.history.back();
    return;
  }
  void import('@capacitor/app').then(({ App }) => App.minimizeApp());
}

async function ensureNativeHardwareBackListener(): Promise<void> {
  if (!Capacitor.isNativePlatform() || listenerInstalled) return;
  const { App } = await import('@capacitor/app');
  const handle = await App.addListener('backButton', ({ canGoBack }) => {
    dispatchNativeHardwareBack(canGoBack);
  });
  removeCapacitorListener = () => void handle.remove();
  listenerInstalled = true;
}

/** Installs the Android hardware back listener once (no-op on web). */
export function installNativeHardwareBackButtonListener(): void {
  void ensureNativeHardwareBackListener();
}

/** Tests only: clears handlers and removes the Capacitor listener. */
export function resetNativeHardwareBackButtonForTests(): void {
  handlers.length = 0;
  removeCapacitorListener?.();
  removeCapacitorListener = null;
  listenerInstalled = false;
}

/** Tests only: runs the same dispatch chain as the native listener. */
export function dispatchNativeHardwareBackForTests(canGoBack = false): void {
  dispatchNativeHardwareBack(canGoBack);
}
